import {
    DropdownListStyled,
    MobileTimeOffDropdown,
    MobileTimeOffTrigger,
    StyledDropdown,
} from "features/timesheet/timesheet-grid/TimesheetGridCellDropdown.styles";
import {
    InputFieldWrapper,
    ReadOnlyCellWrapper,
    StyledTextCell,
    StyledTextInput,
    StyledTimesheetCell,
} from "features/timesheet/timesheet-grid/TimesheetGridCell.styles";
import React, { useContext, useEffect, useRef, useState } from "react";
import { format, isBefore } from "date-fns";

import { AllotmentTypes } from "shared/constants/timesheetEnums";
import { COLOR_VALUES } from "shared/config/colors";
import CircularProgress from "@material-ui/core/CircularProgress";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { DAY_HOURS_LIMIT } from "features/timesheet/timesheet-grid/TimesheetGrid";
import { FEEDBACK_TYPES } from "shared/config/feedbackTypes";
import Icon from "shared/components/utils/Icon";
import PropTypes from "prop-types";
import SessionContext from "app/contexts/SessionContext";
import SubmitTimesheetContext from "features/timesheet/contexts/SubmitTimesheetContext";
import TimesheetGridContext from "features/timesheet/contexts/TimesheetGridContext";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import WeekNavigatorContext from "shared/contexts/WeekNavigatorContext";
import moment from "moment";
import { sendNotification } from "shared/components/feedback/notification/notification";
import useSmallBreakpoint from "shared/hooks/ui/useSmallBreakpoint";
import useTimesheetGrid from "features/timesheet/timesheet-grid/useTimesheetGrid";

// Options for the time-off dropdown (used for OOO and holiday cells).
// Regular project cells use a free-text numeric input instead.
const timeOffDropdownOptions = [
    { label: "--", value: 0 },
    { label: "4 - Half Day", value: 4 },
    { label: "8 - Full Day", value: 8 },
];

// Wraps an input or dropdown with an overlay spinner while an API save is in flight.
const CellWithLoader = ({ inProgress, children }) => {
    return (
        <InputFieldWrapper>
            {inProgress && (
                <div
                    style={{
                        position: "absolute",
                        top: "1px",
                        right: "10px",
                        zIndex: "5",
                    }}
                >
                    <CircularProgress
                        style={{
                            color: COLOR_VALUES.Gray_2,
                            height: "12px",
                            width: "12px",
                        }}
                    />
                </div>
            )}
            {children}
        </InputFieldWrapper>
    );
};

// Returns the matching dropdown option value for a given timeSpan,
// or undefined if the value is not one of the fixed options (0, 4, 8).
const getTimeOffDropdownValue = (val) => {
    const value = timeOffDropdownOptions.find((f) => f.value === val)?.value;
    return value;
};

// Rounds a decimal hour value to the nearest 0.25 increment.
// The switch covers five bands: [0–0.12] → 0, [0.13–0.37] → 0.25, etc.
// Also updates the input display value immediately via setInputValue.
const roundToNearestQuarter = (beforeDecimal, floatVal, setInputValue) => {
    switch (true) {
        case floatVal <= 0.12:
            setInputValue(beforeDecimal);
            return beforeDecimal;
        case floatVal >= 0.13 && floatVal <= 0.37:
            setInputValue(beforeDecimal + 0.25);
            return beforeDecimal + 0.25;
        case floatVal >= 0.38 && floatVal <= 0.62:
            setInputValue(beforeDecimal + 0.5);
            return beforeDecimal + 0.5;
        case floatVal >= 0.63 && floatVal <= 0.87:
            setInputValue(beforeDecimal + 0.75);
            return beforeDecimal + 0.75;
        case floatVal >= 0.88:
            setInputValue(beforeDecimal + 1);
            return beforeDecimal + 1;
        default:
            return null;
    }
};

/**
 * Cell — a single day column within a timesheet project row.
 *
 * Renders one of three modes depending on props:
 *  1. Read-only  — when readOnlyCell is true (approved/rejected/out-of-date/future time-off).
 *                  Displays the saved value inside a tooltip.
 *  2. Dropdown   — when isTimeOff or isHolidayData is true.
 *                  Fixed options: --, 4 (half day), 8 (full day).
 *                  On mobile (≤799 px) a bottom-sheet modal replaces the inline dropdown.
 *  3. Text input — for regular project hours. Accepts decimals, rounds to 0.25 on blur.
 *
 * Props:
 *  data             — cell data from projectHours (timeDay, timeSpan, isWeekendCellDisable, …)
 *  projectId        — legacy job/project ID used when saving hours
 *  isTimeOff        — true when the cell belongs to an OOO / time-off row
 *  readOnlyCell     — true when the cell must not be edited
 *  isHolidayData    — true when the cell belongs to a holiday row
 *  vacationRequestId — linked vacation request, if any
 *  tooltipText      — message shown on read-only cells
 *  reasonId         — OOO reason ID used when saving time-off hours
 *  projectHours     — full week hours array (used to calculate holiday totals for validation)
 */
const Cell = ({
    data,
    projectId,
    isTimeOff,
    readOnlyCell,
    isHolidayData,
    vacationRequestId,
    tooltipText,
    reasonId,
    projectHours,
}) => {
    // Warning messages shown as toast notifications on validation failure.
    const OVERLAPS_PENDING_HR_TIME_OFF_MESSAGE =
        "You already have an HR pending request for this day. Please, contact talentsupport@rga.com";
    const MAX_HOURS_EXCEEDED_TIME_OFF_MESSAGE =
        "You have entered more than 8 time-off hours for a day. Please reduce the time-off hours";
    const OVERLAPS_APPROVED_TIME_OFF_MESSAGE =
        "You already have an approved request for this day";
    const ALLOTMENT_EXCEEDED_TIME_OFF_MESSAGE =
        "Unpaid leave requested. Follow up your request in Time-off tab";

    const session = useContext(SessionContext);
    const { selectedTimesheetStartDate } = useContext(WeekNavigatorContext);
    const {
        selectedDateTimesheetStatus, // true when the timesheet for this week has been submitted
        setHoursUpdateState,
        setHoursUpdateInProgress,
    } = useContext(SubmitTimesheetContext);

    const {
        pendingHRTimeOffInWeek, // HR-pending time-off requests for the selected week
        approvedTimeOffInWeek, // approved time-off requests for the selected week
        holidays, // holiday data for the selected week
        setDisableSubmitTimesheetButton,
        usedAllotment, // PTO/FTO allotment usage for the current year
    } = useContext(TimesheetGridContext);

    const { updateHoursTimeEntries } = useTimesheetGrid();

    // Displayed value for the text input (regular project cells).
    const [inputValue, setInputValue] = useState(data?.timeSpan || "");

    // Ref mirror of inputValue — used inside async handlers to always read
    // the latest typed value without capturing stale closure state.
    const inputValRef = useRef("");

    // True when the user has changed the input value but not yet saved it.
    const [hoursUpdated, setHoursUpdated] = useState(false);

    // True while an API save call is in flight; drives the spinner overlay.
    const [inProgress, setInProgress] = useState(false);

    // Tracks whether the last key pressed was Backspace, so the input
    // handler can clear the field even though the regex won't match "".
    const backspaceKeyDownRef = useRef(false);

    // --- Stale-value fix ---
    // After a successful save, React Query refetches the grid data. Between
    // the moment inProgress becomes false and the moment the new data prop
    // arrives, the useEffects below would momentarily reset the displayed
    // value to the old data.timeSpan — causing a visible flash.
    //
    // savedValueRef holds the value that was just saved. While it is set,
    // the sync effects skip the reset. Once data.timeSpan catches up to
    // the saved value (refetch complete), the ref is cleared and normal
    // sync resumes.
    const savedValueRef = useRef(null);

    // Controls the hover-icon visibility on the desktop dropdown.
    const [isShown, setIsShown] = useState(false);

    // Dropdown options disabled based on current selection (e.g. half-day
    // blocks full-day when another half-day request exists).
    const [disabledOptionsList, setDisabledOptionsList] = useState([]);

    // Time Off mobile dropdown state
    const isSmallScreen = useSmallBreakpoint();
    const [isMobileTimeOffModalOpen, setIsMobileTimeOffModalOpen] =
        useState(false);

    // Displayed value for the time-off / holiday dropdown.
    const [dropdownSelectedValue, setDropdownSelectedValue] = useState(
        getTimeOffDropdownValue(data?.timeSpan)
    );

    // Sync text input value when the cell data changes from outside
    // (e.g. week navigation, background refetch after another cell saves).
    // Guarded by inProgress and savedValueRef to prevent flashing the stale
    // value while a save is in flight or the refetch hasn't landed yet.
    useEffect(() => {
        if (inProgress) return;
        if (savedValueRef.current === null) {
            setInputValue(data?.timeSpan || "");
        } else if (data?.timeSpan === savedValueRef.current) {
            // A save just completed. Wait until data reflects the new value
            // before clearing the guard and allowing external resets.
            savedValueRef.current = null;
        }
        // Don't overwrite inputValue — keep showing the saved value.
    }, [data, selectedTimesheetStartDate, inProgress]);

    // When the user selects a half-day time-off option, disable the full-day
    // option to prevent exceeding 8 hours for the day.
    useEffect(() => {
        if (dropdownSelectedValue === 4 && isTimeOff) {
            setDisabledOptionsList([{ label: "8 - Full Day", value: 8 }]);
        } else {
            setDisabledOptionsList([]);
        }
    }, [dropdownSelectedValue, isTimeOff]);

    // Sync dropdown value when cell data changes from outside.
    // Same stale-value guard as the text input effect above.
    useEffect(() => {
        if (inProgress) return;
        if (savedValueRef.current === null) {
            setDropdownSelectedValue(getTimeOffDropdownValue(data?.timeSpan));
        } else if (data?.timeSpan === savedValueRef.current) {
            // Wait for the refetch to reflect the saved value before resuming
            // normal sync, to prevent the dropdown flashing the old option.
            savedValueRef.current = null;
        }
        // Don't overwrite dropdownSelectedValue — keep showing the saved value.
    }, [data, selectedTimesheetStartDate, inProgress]);

    // Handles every keystroke in the text input.
    // Only digits and dots are allowed; Backspace clears the field via the ref.
    // Warns and disables submit if the entered value exceeds 24 hours.
    const handleOnInputChange = (e) => {
        let checkCondition = e.target.value.match(/^[0-9.\b]+$/)?.length === 1;

        if (checkCondition || backspaceKeyDownRef.current) {
            const value = checkCondition
                ? e.target.value
                : backspaceKeyDownRef.current && "";

            setInputValue(value);
            inputValRef.current = value;
            if (Number(value) === data?.timeSpan) {
                setHoursUpdated(false);
            } else {
                setHoursUpdated(true);
            }

            if (value > 24) {
                setDisableSubmitTimesheetButton(true);
                sendNotification(
                    "More than 24 hours have been entered to a day. Please reduce the hours for that day.",
                    {
                        duration: 5000,
                        close: true,
                        action: false,
                        icon: true,
                        feedbackIcon: "warning",
                    }
                );
            } else {
                setDisableSubmitTimesheetButton(false);
            }
        }
    };

    const errorCondition = Number(inputValRef.current) > DAY_HOURS_LIMIT;

    // Triggered when the user clicks outside the cell or presses Enter/Tab.
    // Rounds the entered value to the nearest 0.25, then saves via API.
    // After the save resolves, savedValueRef is set to prevent the sync
    // effects from resetting the display to stale data during the refetch.
    const handleClickAway = async () => {
        if (hoursUpdated && !errorCondition) {
            setHoursUpdated(false);
            setHoursUpdateState(true);
            const val = Number(inputValRef.current);
            let inputValUpdate = val;
            const valCheck = val - Math.floor(val);
            let beforeDecimal = inputValRef.current?.toString().split(".")[0];
            beforeDecimal = beforeDecimal && Number(beforeDecimal);

            // Round to nearest quarter-hour if a decimal was entered.
            if (valCheck !== 0) {
                const floatVal = Number.parseFloat(valCheck).toFixed(2);
                const rounded = roundToNearestQuarter(
                    beforeDecimal,
                    floatVal,
                    setInputValue
                );
                if (rounded === null) return;
                inputValUpdate = rounded;
            }

            if (inputValUpdate >= 0) {
                setInProgress(true);
                setHoursUpdateInProgress(true);

                let entryDateVal = format(new Date(data.timeDay), "yyyy-MM-dd");
                let requestData = {
                    employeeId: session.legacyId,
                    jobId: projectId,
                    titleId: session.currentLegacyTitleId,
                    entryDate: entryDateVal,
                    hours: Number(inputValUpdate),
                    vacationRequestId:
                        vacationRequestId == null ? 0 : vacationRequestId,
                    agencyId: session.agencyId,
                    isHoliday: !!isHolidayData,
                    officeId: session.currentOfficeId,
                    reasonId: reasonId ?? 0,
                };
                await updateHoursTimeEntries.mutateAsync(requestData);

                // Pin the displayed value to what was just saved so the cell
                // doesn't flash back to the old value while React Query refetches.
                savedValueRef.current = inputValUpdate;
                setInputValue(inputValUpdate || "");

                setInProgress(false);
                setHoursUpdateInProgress(false);
            }
        }
    };

    // Triggers handleClickAway on Enter/Tab so the user doesn't have to click away.
    // Tracks Backspace to allow clearing the field (bypasses the digit-only regex).
    const handleOnKeyDown = (e) => {
        if (e.key === "Backspace") {
            backspaceKeyDownRef.current = true;
        } else {
            backspaceKeyDownRef.current = false;
        }

        if (e.key === "Enter" || e.key === "Tab") {
            handleClickAway();
        }
    };

    // Saves hours for time-off / holiday dropdown cells.
    // Called by handleOnChangeDropdown after validation passes.
    // savedValueRef is set after the save to guard against the stale-value
    // flash described above (same pattern as handleClickAway).
    const saveHoursCall = async (hoursVal) => {
        setInProgress(true);
        setHoursUpdateInProgress(true);

        let date =
            data.timeDay == null ? new Date(data.Date) : new Date(data.timeDay);
        let entryDateVal = format(date, "yyyy-MM-dd");
        let requestData = {
            employeeId: session.legacyId,
            jobId: projectId,
            titleId: session.currentLegacyTitleId,
            entryDate: entryDateVal,
            hours: Number.isNaN(hoursVal) ? 0 : Number(hoursVal),
            vacationRequestId:
                vacationRequestId == null ? 0 : vacationRequestId,
            agencyId: session.agencyId,
            isHoliday: !!isHolidayData,
            officeId: session.currentOfficeId,
            reasonId: reasonId ?? 0,
        };
        await updateHoursTimeEntries.mutateAsync(requestData);

        // Pin the saved value so the dropdown doesn't revert to the old
        // option while React Query refetches the grid data.
        savedValueRef.current = Number.isNaN(hoursVal) ? 0 : Number(hoursVal);

        setInProgress(false);
        setHoursUpdateInProgress(false);
    };

    // Handles dropdown selection changes for time-off / holiday cells.
    // Runs validation before saving; clears to 0 is always allowed.
    const handleOnChangeDropdwon = (newVal) => {
        if (newVal === 0 || isTimeOffValid(newVal)) {
            allotmentWarning(newVal);
            setDropdownSelectedValue(newVal);
            saveHoursCall(newVal);
        }
    };

    // Validates that the new time-off hours don't overlap an existing
    // multi-day request or exceed the 8-hour daily limit.
    const isTimeOffValid = (hours) => {
        if (isTimeOff || isHolidayData) {
            if (
                overlapsInMiddleOfRequest(
                    pendingHRTimeOffInWeek,
                    OVERLAPS_PENDING_HR_TIME_OFF_MESSAGE
                )
            ) {
                return false;
            }

            if (
                overlapsInMiddleOfRequest(
                    approvedTimeOffInWeek,
                    OVERLAPS_APPROVED_TIME_OFF_MESSAGE
                )
            ) {
                return false;
            }

            if (isMaxHoursForTimeOffExceeded(hours)) {
                return false;
            }
        }
        return true;
    };

    // Shows a warning toast if the selected PTO hours would exceed the
    // employee's remaining allotment for the year.
    // Skipped when saving against an existing vacation request (already accounted for).
    const allotmentWarning = (hours) => {
        if (vacationRequestId) return;

        const ptoData = usedAllotment.find(
            (x) =>
                x.allotmentTypeId === AllotmentTypes.PTO &&
                x.reasonId === reasonId
        );

        if (isTimeOff && ptoData && hours !== 0) {
            const remaining = ptoData.allotment - ptoData.daysUsed - hours / 8;
            if (remaining < 0) {
                sendWarningNotification(ALLOTMENT_EXCEEDED_TIME_OFF_MESSAGE);
            }
        }
    };

    // Checks whether the total time-off hours for the current day (combining
    // holidays, approved requests, and pending HR requests) would exceed 8
    // after adding the new value.
    const isMaxHoursForTimeOffExceeded = (hours) => {
        // check for exceeded with holidays
        // if the user is changing a holiday we don't need to calculate this.
        let totalHoursHolidays = 0;
        if (!isHolidayData) {
            totalHoursHolidays = getSavedHolidaysForCurrentDay();
            if (totalHoursHolidays + hours > 8) {
                sendWarningNotification(MAX_HOURS_EXCEEDED_TIME_OFF_MESSAGE);
                return true;
            }
        }

        // check for exceeded with already approved requests
        const totalHoursApproved = getTotalHoursInCurrentDay(
            approvedTimeOffInWeek
        );
        if (totalHoursApproved + hours > 8) {
            sendWarningNotification(MAX_HOURS_EXCEEDED_TIME_OFF_MESSAGE);
            return true;
        }

        // check for exceeded with pending HR requests
        const totalHoursPendingHR = getTotalHoursInCurrentDay(
            pendingHRTimeOffInWeek
        );
        if (totalHoursPendingHR + hours > 8) {
            sendWarningNotification(OVERLAPS_PENDING_HR_TIME_OFF_MESSAGE);
            return true;
        }

        // check for exceeded all together
        if (
            totalHoursPendingHR +
                totalHoursApproved +
                totalHoursHolidays +
                hours >
            8
        ) {
            sendWarningNotification(MAX_HOURS_EXCEEDED_TIME_OFF_MESSAGE);
            return true;
        }

        return false;
    };

    // Returns true if the cell's date falls strictly between the first and
    // last day of a multi-day request (i.e. not on the boundary days).
    // Boundary days are handled separately via getTotalHoursInCurrentDay.
    const overlapsInMiddleOfRequest = (requests, message) => {
        if (requests) {
            const currentDate = data.timeDay
                ? moment(data.timeDay).format("YYYY-MM-DD")
                : moment(data.Date).format("YYYY-MM-DD");

            const overlaps = requests.some((x) => {
                // Don't block edits to the cell that owns this request.
                if (
                    vacationRequestId &&
                    vacationRequestId === x.vacationRequestId
                )
                    return false;

                const FD = moment(x.firstDayOut).format("YYYY-MM-DD");
                const LD = moment(x.lastDayOut).format("YYYY-MM-DD");

                // Single-day requests don't have a "middle" — skip.
                if (moment(FD).isSame(LD)) {
                    return false;
                }
                const isBetween =
                    moment(currentDate).isAfter(FD) &&
                    moment(currentDate).isBefore(LD);
                return isBetween;
            });

            if (overlaps) {
                sendWarningNotification(message);
                return true;
            }
        }
        return false;
    };

    // Sums the time-off hours for the current day across a list of requests.
    // Only the first and last day of each request contribute hours (4 or 8
    // depending on morning/afternoon flags). Middle days are handled by
    // overlapsInMiddleOfRequest and are blocked entirely.
    const getTotalHoursInCurrentDay = (requests) => {
        let totalHours = 0;
        const currentDate = data.timeDay
            ? moment(data.timeDay).format("YYYY-MM-DD")
            : moment(data.Date).format("YYYY-MM-DD");

        requests?.forEach((x) => {
            if (vacationRequestId && vacationRequestId === x.vacationRequestId)
                return false;

            let FD = moment(x.firstDayOut).format("YYYY-MM-DD");
            let LD = moment(x.lastDayOut).format("YYYY-MM-DD");

            if (moment(currentDate).isSame(FD)) {
                totalHours += x.fdMorning && x.fdAfternoon ? 8 : 4;
            } else if (!moment(LD).isSame(FD)) {
                if (moment(currentDate).isSame(LD)) {
                    totalHours += x.ldMorning && x.ldAfternoon ? 8 : 4;
                }
            }
        });

        return totalHours;
    };

    // Returns the sum of already-saved holiday hours for this cell's date,
    // used to check whether adding time-off hours would exceed the 8-hour cap.
    const getSavedHolidaysForCurrentDay = () => {
        if (!holidays?.Holidays) {
            return 0;
        }

        const currentDate = moment(data.timeDay).format("YYYY-MM-DD");

        // get TO projects
        const toProjects = projectHours
            .filter((x) => x.isHoliday)
            .map((x) => x.hours);
        const toProjectsFlatted = toProjects.flat();

        // get Saved hours
        const hoursSaved = toProjectsFlatted.filter(
            (x) => x.hasOwnProperty("Id") || x.hasOwnProperty("jobId")
        );

        // get saved hours on current date
        const sameDate = hoursSaved.filter((x) => {
            const date = x.timeDay ? x.timeDay : x.Date;
            return moment(currentDate).isSame(
                moment(date).format("YYYY-MM-DD")
            );
        });

        const allHours = sameDate.map((x) => x.timeSpan);
        const total = allHours.reduce((a, b) => a + b, 0);

        return total;
    };

    const sendWarningNotification = (message) => {
        sendNotification(message, {
            duration: 5000,
            close: true,
            action: false,
            icon: true,
            feedbackIcon: "warning",
        });
    };

    // Cells before the employee's start date are always disabled and show "--".
    const tenureNotStarted = isBefore(
        new Date(data.timeDay),
        new Date(session.startDate)
    );

    const TimesheetCellComponent = StyledTimesheetCell;

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <TimesheetCellComponent
                disabled={
                    tenureNotStarted ||
                    selectedDateTimesheetStatus || // timesheet already submitted
                    (isHolidayData && !data?.timeSpan) || // holiday with no saved hours is non-editable
                    data.isWeekendCellDisable ||
                    readOnlyCell
                }
            >
                {/* Read-only mode: show saved value inside a tooltip explaining why it's locked */}
                {data &&
                    !tenureNotStarted &&
                    !data.isWeekendCellDisable &&
                    readOnlyCell && (
                        <Tooltip enterDelay={0} text={tooltipText}>
                            <ReadOnlyCellWrapper>
                                <StyledTextCell
                                    variant="Body_2_2"
                                    color="Gray_1"
                                >
                                    {data.timeSpan}
                                </StyledTextCell>
                            </ReadOnlyCellWrapper>
                        </Tooltip>
                    )}

                {/* Dropdown mode: time-off and holiday cells use fixed 0 / 4 / 8 options.
                        On mobile, the inline dropdown is replaced by a bottom-sheet modal. */}
                {data &&
                    !tenureNotStarted &&
                    !readOnlyCell &&
                    (isTimeOff || isHolidayData) &&
                    !data.isWeekendCellDisable && (
                        <CellWithLoader inProgress={inProgress}>
                            {isSmallScreen ? (
                                <>
                                    <MobileTimeOffTrigger
                                        onClick={() =>
                                            setIsMobileTimeOffModalOpen(true)
                                        }
                                        disabled={selectedDateTimesheetStatus}
                                        $hasSelection={!!dropdownSelectedValue}
                                    >
                                        <span>
                                            {timeOffDropdownOptions.find(
                                                (opt) =>
                                                    opt.value ===
                                                    dropdownSelectedValue
                                            )?.label || "--"}
                                        </span>
                                        <Icon
                                            name="arrowSelectDownIcon"
                                            type="OUTLINE"
                                            size={16}
                                        />
                                    </MobileTimeOffTrigger>

                                    <MobileTimeOffDropdown
                                        isOpen={isMobileTimeOffModalOpen}
                                        onClose={() =>
                                            setIsMobileTimeOffModalOpen(false)
                                        }
                                        options={timeOffDropdownOptions}
                                        value={dropdownSelectedValue}
                                        onChange={handleOnChangeDropdwon}
                                        disabled={selectedDateTimesheetStatus}
                                    />
                                </>
                            ) : (
                                <>
                                    <DropdownListStyled />
                                    <StyledDropdown
                                        value={dropdownSelectedValue}
                                        displayValueOnButton={true}
                                        options={timeOffDropdownOptions}
                                        onChange={(val) =>
                                            handleOnChangeDropdwon(val)
                                        }
                                        placeholder="--"
                                        disabledOptionsList={
                                            disabledOptionsList
                                        }
                                        $hasSelection={!!dropdownSelectedValue}
                                        showIcon={false}
                                        showIconOnHover={isShown}
                                        onMouseEnter={() => setIsShown(true)}
                                        onMouseLeave={() => setIsShown(false)}
                                        disabled={selectedDateTimesheetStatus}
                                    />
                                </>
                            )}
                        </CellWithLoader>
                    )}

                {/* Text input mode: regular project cells accept free-form decimal hours. */}
                {data &&
                    !tenureNotStarted &&
                    !readOnlyCell &&
                    !isTimeOff &&
                    !isHolidayData && (
                        <CellWithLoader inProgress={inProgress}>
                            <StyledTextInput
                                placeholder="--"
                                value={inputValue}
                                onChange={handleOnInputChange}
                                onKeyDown={handleOnKeyDown}
                                onFocus={(e) => (e.target.placeholder = "")}
                                onBlur={(e) => {
                                    e.target.placeholder = "--";
                                    if (Number(e.target.value) === 0) {
                                        setInputValue("");
                                    }
                                }}
                                feedbackType={
                                    errorCondition
                                        ? FEEDBACK_TYPES.Error
                                        : FEEDBACK_TYPES.Success
                                }
                                disabled={selectedDateTimesheetStatus}
                            />
                        </CellWithLoader>
                    )}

                {/* Fallback for cells with no data or before tenure start: show "--" */}
                {((!data && !data.isWeekendCellDisable) ||
                    tenureNotStarted) && (
                    <StyledTextCell variant="Body_2_1" color="Gray_4">
                        &#45;&#45;
                    </StyledTextCell>
                )}
            </TimesheetCellComponent>
        </ClickAwayListener>
    );
};

CellWithLoader.propTypes = {
    inProgress: PropTypes.bool,
    children: PropTypes.node,
};

Cell.propTypes = {
    data: PropTypes.object,
    projectId: PropTypes.number,
    isTimeOff: PropTypes.bool,
    readOnlyCell: PropTypes.bool,
    isHolidayData: PropTypes.bool,
    vacationRequestId: PropTypes.number,
    tooltipText: PropTypes.string,
    reasonId: PropTypes.number,
    projectHours: PropTypes.array,
};

export default Cell;
